import { NgRedux } from '@angular-redux/store';
import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { NotificationFacade, PaymentService, PurchaseFacade, SwishFacade } from '@ridango/components';
import { saveCartState, saveTempState } from '@ridango/components/lib/localStorage';
import { PaymentMethodsFacade } from '@ridango/components/lib/payment-methods/+state';
import { ShoppingCartFacade } from '@ridango/components/lib/shopping-cart/+state';
import {
    AppStartupService,
    AuthService,
    InitiatePaymentRequest,
    PaymentChannelCode,
    PaymentGatewayService,
    PaymentMethod,
    PaymentPortalService
} from '@ridango/core';

import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Env from '@ridango/core/env';
import { BaseComponent } from '@ridango/core';
@Component({
    selector: 'rwidget-cart-summary',
    templateUrl: './cart-summary.component.html'
})
export class CartSummaryComponent extends BaseComponent implements OnInit, OnDestroy {
    navigateAwaySelection$: Subject<boolean> = new Subject<boolean>();

    cart$ = this.cartFacade.cart$;
    selectedPaymentMethod$ = this.paymentFacade.selectedPaymentMethod$;
    swishAccountId$ = this.swishFacade.accountId$;
    termsAgreed$ = this.cartFacade.termsAgreed$;
    swishError$ = this.swishFacade.error$;
    showNavigationModal = false;

    swishAccountId;
    token = '';
    inWebPaymentSelected = false;
    invoiceRequested: boolean;

    cart = null;
    selectedPaymentMethod: PaymentMethod = null;
    paymentChannelCode = PaymentChannelCode;

    @Output() swishIdInvalid = new EventEmitter<any>();

    constructor(
        @Inject('ENV') public env: Env,
        private cartFacade: ShoppingCartFacade,
        private paymentFacade: PaymentMethodsFacade,
        private purchaseFacade: PurchaseFacade,
        private notifications: NotificationFacade,
        private swishFacade: SwishFacade,
        private ridangoAuth: AuthService,
        private ngRedux: NgRedux<any>,
        public appStartupService: AppStartupService,
        private paymentPortalService: PaymentPortalService,
        private paymentService: PaymentService,
        private translate: TranslateService,
        private router: Router,
        private paymentGatewayService: PaymentGatewayService
    ) {
        super();
    }

    ngOnInit() {
        this.swishAccountId$.pipe(takeUntil(this.componentDestroyed$)).subscribe((id) => (this.swishAccountId = id));

        this.token = this.ridangoAuth.getLocalAccessToken();

        if (this.ridangoAuth.getLoginStatus()) {
            this.cartFacade.setCartAgreement(true);
        }

        this.cart$.pipe(takeUntil(this.componentDestroyed$)).subscribe((c) => {
            this.cart = c;
        });

        this.selectedPaymentMethod$.pipe(takeUntil(this.componentDestroyed$)).subscribe((pm) => {
            this.selectedPaymentMethod = pm;
        });

        this.swishError$.pipe(takeUntil(this.componentDestroyed$)).subscribe((error) => {
            switch (error?.error) {
                case 'SWISH_PAYMENT_REQUEST_ERROR_FF08':
                case 'SWISH_PAYMENT_REQUEST_ERROR_RP03':
                case 'SWISH_PAYMENT_REQUEST_ERROR_BE18':
                case 'SWISH_PAYMENT_REQUEST_ERROR_RP01':
                case 'SWISH_PAYMENT_REQUEST_ERROR_PA02':
                case 'SWISH_PAYMENT_REQUEST_ERROR_AM06':
                case 'SWISH_PAYMENT_REQUEST_ERROR_AM02':
                case 'SWISH_PAYMENT_REQUEST_ERROR_AM03':
                case 'SWISH_PAYMENT_REQUEST_ERROR_RP02':
                case 'SWISH_PAYMENT_REQUEST_ERROR_RP06':
                case 'SWISH_PAYMENT_REQUEST_ERROR_ACMT03':
                case 'SWISH_PAYMENT_REQUEST_ERROR_ACMT01':
                case 'SWISH_PAYMENT_REQUEST_ERROR_ACMT07':
                case 'SWISH_PAYMENT_REQUEST_ERROR_UNKW':
                case 'SWISH_PAYMENT_REQUEST_ERROR_VR01':
                case 'SWISH_PAYMENT_REQUEST_ERROR_VR02':
                    return this.notifications.error(this.translate.instant(error?.error));
                default:
                    return error && this.notifications.error(this.translate.instant('SWISH_PAYMENT_REQUEST_ERROR'));
            }
        });

        this.token = this.ridangoAuth.getLocalAccessToken();
    }

    confirmSwish() {
        this.notifications.removeAllExceptNext();
        this.inWebPaymentSelected = true;
        if (!this.swishAccountId || !new RegExp(/^([+]467|07)\d{8}$/, 'g').test(this.swishAccountId)) {
            this.swishIdInvalid.emit();
        } else {
            if (this.swishAccountId.startsWith('0')) {
                this.swishAccountId = this.swishAccountId.substring(1);
            }
            if (!this.swishAccountId.startsWith('+46')) {
                this.swishAccountId = '+46' + this.swishAccountId;
            }
            this.swishFacade.startTransaction(this.cart, this.selectedPaymentMethod, this.swishAccountId);
        }
    }

    confirmPaymentPortal() {
        if (!this.cart?.shopping_cart_id) {
            this.notifications.error(this.translate.instant('LOCAL_NO_CART_FOUND'));
            window.scroll(0, 0);
        }

        if (!this.cart.termsAgreed) {
            window.scroll(0, 0);
            this.notifications.error(this.translate.instant('LOCAL_MUST_AGREE_TERMS'));
        }

        if (!this.selectedPaymentMethod?.payment_id) {
            window.scroll(0, 0);
            this.notifications.error(this.translate.instant('SHOPPING_CART_CHOOSE_PAYMENT_METHOD'));
        }

        this.inWebPaymentSelected = true;
        saveTempState(this.ngRedux.getState());
        this.cartFacade.setCartStatus('L');
        saveCartState(this.cart);
        const initiatePaymentRequest: InitiatePaymentRequest = {
            shoppingCartId: this.cart.shopping_cart_id,
            paymentId: this.selectedPaymentMethod.payment_id
        };
        this.paymentPortalService
            .initiatePayment(initiatePaymentRequest)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe();
    }

    confirmCart() {
        if (!this.cart || !this.cart.shopping_cart_id) {
            this.notifications.error(this.translate.instant('LOCAL_NO_CART_FOUND'));
            window.scroll(0, 0);
        }
        if (!this.ridangoAuth.getLoginStatus() && !this.cart.termsAgreed) {
            window.scroll(0, 0);
            this.notifications.error(this.translate.instant('LOCAL_MUST_AGREE_TERMS'));
            return;
        }
        if (this.cart?.shopping_cart_id) {
            saveTempState(this.ngRedux.getState());
            this.cartFacade.setCartStatus('L');
            saveCartState(this.cart);
        }
        this.inWebPaymentSelected = true;
        this.paymentGatewayService.createPaymentIntent({
            banklinkAccountId: this.selectedPaymentMethod.banklink_account_id,
            code: this.selectedPaymentMethod.code,
            paymentId: this.selectedPaymentMethod.payment_id,
            shoppingCartId: this.cart.shopping_cart_id,
            lang: this.translate.currentLang,
            returnUrl: this.env.PAYMENT_RETURN_URL + this.translate.currentLang + this.env.PAYMENT_RETURN_COMPONENT,
            authToken: this.token
        });
    }

    confirmAdyen() {
        if (this.cart && this.cart.shopping_cart_id) {
            this.inWebPaymentSelected = true;
            saveTempState(this.ngRedux.getState());
            this.cartFacade.setCartStatus('L');
            saveCartState(this.cart);
        }
    }

    confirmInvoice() {
        this.invoiceRequested = true;
        this.inWebPaymentSelected = true;
        saveTempState(this.ngRedux.getState());
        this.cartFacade.setCartStatus('L');
        saveCartState(this.cart);

        this.paymentService
            .requestInvoice(this.cart, this.selectedPaymentMethod, this.translate.currentLang)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(
                () => {
                    this.router.navigate([this.translate.currentLang, 'purchase', 'result'], {
                        queryParams: { invoiceResult: 'S' }
                    });
                },
                () => {
                    this.router.navigate([this.translate.currentLang, 'purchase', 'result'], {
                        queryParams: { invoiceResult: 'F' }
                    });
                }
            );
    }

    cancelCart() {
        this.cartFacade.deleteCart();
        this.purchaseFacade.unsetShoppingCart();
    }

    canDeactivate(): Observable<boolean> | boolean {
        return this.confirm();
    }

    confirm(): Observable<boolean> | boolean {
        if (this.ngRedux.getState().cart.status === 'L' && !this.inWebPaymentSelected) {
            this.showNavigationModal = true;
        } else {
            this.inWebPaymentSelected = false;
            return true;
        }
        return this.navigateAwaySelection$;
    }

    onCloseNavigationModal() {
        this.showNavigationModal = false;
        this.navigateAwaySelection$.next(false);
    }

    onConfirmNavigationModal() {
        this.cartFacade.deleteCart();
        this.purchaseFacade.unsetShoppingCart();
        this.navigateAwaySelection$.next(true);
        this.showNavigationModal = false;
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
